/**
 * Parse terminal invitation errors from GET /api/invitation/resolve.
 *
 * Admin returns 410 with a structured body:
 *   { code, status, recipient_email, account_exists }
 * Legacy installs may still return a plain string with ``(status=…)``.
 */

export type InviteTerminalKind = 'consumed' | 'revoked' | 'expired' | 'unknown';

export interface InviteTerminalState {
  kind: InviteTerminalKind;
  message: string;
  email?: string;
  /** Set for ``consumed``; false means invite consumed but no matching user row. */
  accountExists?: boolean;
}

const STATUS_RE = /\(status=(\w+)\)/i;

function parseStatusFromString(detail: string): InviteTerminalKind {
  const match = detail.match(STATUS_RE);
  const raw = match?.[1]?.toLowerCase();
  if (raw === 'consumed' || raw === 'revoked' || raw === 'expired') {
    return raw;
  }
  return 'unknown';
}

function messageForKind(
  kind: InviteTerminalKind,
  accountExists?: boolean,
): string {
  switch (kind) {
    case 'consumed':
      if (accountExists === false) {
        return (
          'This invitation was marked as used, but we could not find your account. ' +
          'Please contact your community office or GetPawtected support for help.'
        );
      }
      return (
        'This invitation link was already used to create your account. ' +
        'Please sign in instead of registering again.'
      );
    case 'revoked':
      return (
        'This invitation link has been revoked. Registration is not available with this link. ' +
        'Contact your community office if you need a new invitation.'
      );
    case 'expired':
      return (
        'This invitation link has expired. Registration is not available with this link. ' +
        'Contact your community office to request a new invitation.'
      );
    default:
      return (
        'This invitation link could not be verified. Registration is not available. ' +
        'Try opening the link from your invitation email again, or contact your community office for help.'
      );
  }
}

function parseAccountExists(raw: unknown, kind: InviteTerminalKind): boolean | undefined {
  if (kind !== 'consumed') {
    return undefined;
  }
  if (typeof raw === 'boolean') {
    return raw;
  }
  return undefined;
}

export function parseInviteResolveError(
  detail: unknown,
  fallback = messageForKind('unknown'),
): InviteTerminalState {
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const obj = detail as Record<string, unknown>;
    const statusRaw = String(obj.status ?? obj.invitation_status ?? '').toLowerCase();
    const kind: InviteTerminalKind =
      statusRaw === 'consumed' || statusRaw === 'revoked' || statusRaw === 'expired'
        ? statusRaw
        : 'unknown';
    const email =
      typeof obj.recipient_email === 'string' ? obj.recipient_email : undefined;
    const accountExists = parseAccountExists(obj.account_exists, kind);
    const message =
      typeof obj.message === 'string'
        ? obj.message
        : messageForKind(kind, accountExists);
    return { kind, message, email, accountExists };
  }

  if (typeof detail === 'string') {
    const kind = parseStatusFromString(detail);
    const accountExists = kind === 'consumed' ? true : undefined;
    return {
      kind,
      message: messageForKind(kind, accountExists),
      accountExists,
    };
  }

  return { kind: 'unknown', message: fallback };
}

/** Short heading for the terminal-state banner on the signup page. */
export function inviteTerminalHeading(state: InviteTerminalState): string {
  if (state.kind === 'consumed' && state.accountExists === false) {
    return 'Invitation issue';
  }
  switch (state.kind) {
    case 'consumed':
      return 'Account already created';
    case 'expired':
      return 'Invitation expired';
    case 'revoked':
      return 'Invitation revoked';
    default:
      return 'Registration unavailable';
  }
}

/** Auto-redirect to login when a consumed invite matches a live user account. */
export function shouldRedirectConsumedInviteToLogin(state: InviteTerminalState): boolean {
  return state.kind === 'consumed' && state.accountExists !== false;
}

/** Login URL for a consumed invite that already has a user row. */
export function consumedInviteLoginPath(state: InviteTerminalState): string {
  const email = state.email?.trim();
  if (email) {
    return loginPathWithEmail(email, 'invite-consumed');
  }
  return '/login?hint=invite-consumed';
}

/** Show primary Sign in CTA on the signup page. */
export function shouldShowInviteSignIn(state: InviteTerminalState): boolean {
  if (state.kind === 'consumed') {
    return state.accountExists !== false;
  }
  // expired/revoked/unknown: no registration — offer sign-in if they already have an account
  return state.kind === 'revoked' || state.kind === 'expired' || state.kind === 'unknown';
}

/** Block registration for any terminal invitation state (prod invite-only policy). */
export function shouldHideSignupForm(state: InviteTerminalState | null): boolean {
  return state !== null;
}

export function isRegisterUserAlreadyExists(detail: unknown): boolean {
  if (typeof detail === 'string') {
    return detail.includes('REGISTER_USER_ALREADY_EXISTS');
  }
  return false;
}

export function loginPathWithEmail(email: string, hint?: string): string {
  const params = new URLSearchParams();
  params.set('email', email);
  if (hint) {
    params.set('hint', hint);
  }
  return `/login?${params.toString()}`;
}
